"use client";
import React, { useMemo, useCallback, memo } from "react";
import styles from "./CarFinance.module.scss";
import { getCarFinanceData } from "./data";
import getIndianCurrencyFormat from "@helpers/getIndianCurrencyFormat";
import type { CarFinanceProps, BankData } from "@interfaces/CarFinance.types";
import { useCarFinanceState } from "./hooks/useCarFinanceState";
import { useCarFinanceLogic } from "./hooks/useCarFinanceLogic";
import { CarFinanceErrorBoundary } from "./ErrorBoundary";
import { FinanceModal } from "./FinanceModal";
import { BenefitsCard } from "./BenefitsCard";
import { SavingsTable } from "./SavingsTable";
import { PartneredBanks } from "./PartneredBanks";
import { LoginButton } from "./LoginButton";
import { Disclaimer } from "./Disclaimer";

export const CarFinance = memo((props: CarFinanceProps) => {
  // Get all data from the data file following data-driven pattern
  const data = getCarFinanceData(props);

  // Memoized calculations
  const bankArray = useMemo(
    () => data.carFinanceBankData.filter((bank) => bank.is_active) || [],
    [data.carFinanceBankData]
  );

  const initialSelectedBank = useMemo(
    () =>
      bankArray.find(
        (bank) => parseFloat(bank.id) === parseFloat(data.selectedBankValue?.id)
      ) ||
      bankArray[0] ||
      null,
    [bankArray, data.selectedBankValue]
  );

  // Custom hooks for state management and business logic
  const {
    financeState,
    modalState,
    inputState,
    tempState,
    updateFinanceState,
    updateInputState,
    updateTempState,
    openModal,
    closeModal,
    updateCarLoanDiscount,
  } = useCarFinanceState({
    onRoadPrice: data.onRoadPrice,
    savingsBreakupData: data.savingsBreakupData,
    initialSelectedBank,
  });

  const {
    ADPrice,
    getTotalEmiSavings,
    totalAckoDriveSavings,
    getRIPPlusConsumer,
    handleDownPaymentChange,
    handleInterestChange,
    handleModalUpdate,
  } = useCarFinanceLogic({
    onRoadPrice: data.onRoadPrice,
    savingsBreakupData: data.savingsBreakupData,
    carFinanceData: data.carFinanceData,
    maxSavings: data.maxSavings,
    initialSelectedBank,
    financeState,
    updateFinanceState,
    updateInputState,
    updateCarLoanDiscount,
    convertAmountToNumber: (val: string) => {
      try {
        if (!val || typeof val !== "string") return 0;
        const cleaned = val.replace(/[^\d]/g, "");
        const num = Number(cleaned);
        return isNaN(num) ? 0 : num;
      } catch (error) {
        console.error("Error converting amount to number:", error);
        return 0;
      }
    },
  });

  // Optimized handler functions
  const handleTenureChange = useCallback(
    (year: number) => {
      updateTempState({ tempLoanTenure: year });
    },
    [updateTempState]
  );

  const handleModalUpdateWrapper = useCallback(() => {
    if (modalState.type) {
      const success = handleModalUpdate(modalState.type, inputState, tempState);
      if (success) {
        closeModal();
      }
    }
  }, [handleModalUpdate, modalState.type, inputState, tempState, closeModal]);

  const handleEditClick = useCallback(
    (mode: "downpayment" | "tenure" | "interest") => {
      if (mode === "downpayment") {
        updateInputState({
          downPaymentInputValue: getIndianCurrencyFormat(
            financeState.downPayment
          ),
          downPaymentError: "",
        });
      } else if (mode === "tenure") {
        updateTempState({ tempLoanTenure: financeState.loanTenure });
      } else if (mode === "interest") {
        updateTempState({
          tempSelectedBank: financeState.selectedBank,
          tempOtherInterest: financeState.otherInterest,
        });
        updateInputState({
          interestInputValue:
            financeState.otherInterest !== ""
              ? String(financeState.otherInterest)
              : "",
          interestError: "",
        });
      }
      openModal(mode);
    },
    [
      financeState.downPayment,
      financeState.loanTenure,
      financeState.selectedBank,
      financeState.otherInterest,
      updateInputState,
      updateTempState,
      openModal,
    ]
  );

  const handleTempBankChange = useCallback(
    (bank: BankData) => {
      updateTempState({
        tempSelectedBank: bank,
        tempOtherInterest: bank?.interest_rate,
      });
      updateInputState({
        interestInputValue: String(bank?.interest_rate || ""),
        interestError: "",
      });
    },
    [updateTempState, updateInputState]
  );

  // Memoized slider configuration
  const sliderBg = useMemo(() => {
    const percent =
      ((financeState.loanTenure - data.sliderConfig.min) /
        (data.sliderConfig.max - data.sliderConfig.min)) *
      100;
    return `linear-gradient(to right, ${data.sliderConfig.fillColor} ${percent}%, ${data.sliderConfig.trackColor} ${percent}%)`;
  }, [financeState.loanTenure, data.sliderConfig]);

  return (
    <CarFinanceErrorBoundary>
      <div className={styles.carFinanceWrapper}>
        {data.isLoggedIn && (
          <FinanceModal
            isOpen={modalState.isOpen}
            type={modalState.type}
            editMode={modalState.editMode}
            onClose={closeModal}
            onUpdate={handleModalUpdateWrapper}
            downPaymentInputValue={inputState.downPaymentInputValue}
            downPaymentError={inputState.downPaymentError}
            onDownPaymentChange={handleDownPaymentChange}
            tempLoanTenure={tempState.tempLoanTenure}
            onTenureChange={handleTenureChange}
            bankArray={bankArray}
            tempSelectedBank={tempState.tempSelectedBank}
            interestInputValue={inputState.interestInputValue}
            interestError={inputState.interestError}
            onBankChange={handleTempBankChange}
            onInterestChange={handleInterestChange}
            isUpdateDisabled={
              (modalState.type === "downpayment" &&
                !!inputState.downPaymentError) ||
              (modalState.type === "interest" && !!inputState.interestError)
            }
          />
        )}

        <BenefitsCard
          carFinanceValuePropositions={data.carFinanceValuePropositions}
        />

        {data.isLoggedIn && (
          <SavingsTable
            isLoggedIn={data.isLoggedIn}
            onRoadPrice={data.onRoadPrice}
            savingsBreakupData={data.savingsBreakupData}
            ADPrice={ADPrice}
            carLoanDiscount={financeState.carLoanDiscount}
            downPayment={financeState.downPayment}
            otherInterest={financeState.otherInterest}
            loanTenure={financeState.loanTenure}
            selectedBank={financeState.selectedBank}
            initialSelectedBank={initialSelectedBank}
            getRIPPlusConsumer={getRIPPlusConsumer}
            getTotalEmiSavings={getTotalEmiSavings}
            totalAckoDriveSavings={totalAckoDriveSavings}
            handleEditClick={handleEditClick}
          />
        )}

        <PartneredBanks bankArray={bankArray} />

        <div className={styles.buttonDisclaimerWrapper}>
          {!data.isLoggedIn && (
            <LoginButton
              pageType={data.pageType}
              carAvailability={data.carAvailability}
              brandName={data.brandName}
              modelName={data.modelName}
              variantName={data.variantName}
              cityName={data.cityName}
            />
          )}
          <Disclaimer />
        </div>
      </div>
    </CarFinanceErrorBoundary>
  );
});

CarFinance.displayName = "CarFinance";
